import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Badge,
  Button,
  Icon,
  BlockStack,
  InlineStack,
  TextField,
  ButtonGroup,
  Spinner,
} from '@shopify/polaris';
import {
  ContentMinor,
  RefreshMajor,
  ChevronLeftMinor,
  ChevronRightMinor,
  ReplayMinor,
} from '@shopify/polaris-icons';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';

// Import services
import { generationsService } from '../services/generationsService';

const GenerationQueuePage = ({ setToastMessage, setToastActive }) => {
  const headings = [
    'Generation ID',
    'Job ID',
    'Name',
    'Status',
    'Created At',
    'Actions',
  ];

  // State for queue data
  const [queueItems, setQueueItems] = useState([]);
  const [queueStats, setQueueStats] = useState(null);
  const [isLoadingQueue, setIsLoadingQueue] = useState(false);
  const [queueStatusTab, setQueueStatusTab] = useState('all');
  const [queueSearchQuery, setQueueSearchQuery] = useState('');
  const [paginationData, setPaginationData] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const itemsPerPage = limit;

  // Retry state
  const [retryingJobs, setRetryingJobs] = useState(new Set());

  // Fetch queue data
  const fetchQueueData = useCallback(async () => {
    setIsLoadingQueue(true);
    try {
      const response = await generationsService.getQueueItems(page, limit);
      const data = response?.data || response;
      
      // Get jobs array from the new API response
      const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
      setQueueItems(jobs);
      
      // Set pagination data from API response
      if (data?.pagination) {
        setPaginationData(data.pagination);
      }
    } catch (err) {
      console.error('Fetch queue error:', err);
      setQueueItems([]);
      setPaginationData(null);
      if (setToastMessage && setToastActive) {
        setToastMessage('Failed to fetch queue data');
        setToastActive(true);
      }
    } finally {
      setIsLoadingQueue(false);
    }
  }, [page, limit, setToastMessage, setToastActive]);

  // Fetch queue stats
  const fetchQueueStats = useCallback(async () => {
    try {
      const response = await generationsService.getQueueStats();
      const data = response?.data || response;
      setQueueStats(data);
    } catch (err) {
      console.error('Fetch queue stats error:', err);
      setQueueStats(null);
    }
  }, []);

  // Retry failed job
  const handleRetryJob = useCallback(async (jobId) => {
    if (!jobId) {
      if (setToastMessage && setToastActive) {
        setToastMessage('Invalid job ID');
        setToastActive(true);
      }
      return;
    }

    setRetryingJobs(prev => new Set([...prev, jobId]));
    
    try {
      await generationsService.retryJob(jobId, {
        checkDependencies: true,
        force: false
      });
      
      if (setToastMessage && setToastActive) {
        setToastMessage('Job retry initiated successfully');
        setToastActive(true);
      }
      
      // Refresh queue data to show updated status
      fetchQueueData();
      fetchQueueStats();
    } catch (error) {
      console.error('Retry job error:', error);
      if (setToastMessage && setToastActive) {
        setToastMessage(`Failed to retry job: ${error.message}`);
        setToastActive(true);
      }
    } finally {
      setRetryingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  }, [fetchQueueData, fetchQueueStats, setToastMessage, setToastActive]);

  useEffect(() => {
    fetchQueueData();
    fetchQueueStats();
  }, [fetchQueueData, fetchQueueStats]);

  // Refetch data when page changes
  useEffect(() => {
    fetchQueueData();
  }, [page]);

  // Filter queue items (status filter only, search will be handled server-side later)
  const filteredQueueItems = queueItems.filter(item => {
    // Status filter
    if (queueStatusTab !== 'all' && item.status !== queueStatusTab) {
      return false;
    }
    
    // Search filter (client-side for now)
    if (queueSearchQuery) {
      const q = queueSearchQuery.toLowerCase();
      return (
        (item.generationId && item.generationId.toLowerCase().includes(q)) ||
        (item.jobId && item.jobId.toLowerCase().includes(q)) ||
        (item.name && item.name.toLowerCase().includes(q))
      );
    }
    
    return true;
  });

  // Use server-side pagination data
  const totalItems = paginationData?.totalItems || queueItems.length;
  const totalPages = paginationData?.totalPages || Math.ceil(totalItems / limit);
  const currentPage = paginationData?.currentPage || page;
  const hasNextPage = paginationData?.hasNextPage || false;
  const hasPrevPage = paginationData?.hasPrevPage || false;
  const paginatedItems = filteredQueueItems; // Already paginated by server

  // Status badge color mapping
  const getStatusBadge = (status) => {
    const statusColors = {
      queued: 'info',
      running: 'attention',
      completed: 'success',
      failed: 'critical'
    };
    
    return (
      <Badge tone={statusColors[status] || 'info'}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
      </Badge>
    );
  };

  // Generate pagination buttons
  const getVisiblePages = () => {
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);
    start = Math.max(1, end - 4);
    
    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Layout style={{ marginBottom: '24px' }}>
      <Layout.Section>
        <Card>
          <div>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingMd">Queue Monitor</Text>
                <Button
                  onClick={() => {
                    fetchQueueData();
                    fetchQueueStats();
                  }}
                  loading={isLoadingQueue}
                  icon={RefreshMajor}
                >
                  Refresh
                </Button>
              </InlineStack>
              
              {queueStats && (
                <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <InlineStack gap="600" align="start">
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingLg" color="success">{queueStats.queued || 0}</Text>
                      <Text variant="bodySm" tone="subdued">Queued</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingLg" color="warning">{queueStats.running || 0}</Text>
                      <Text variant="bodySm" tone="subdued">Running</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingLg" color="success">{queueStats.completed || 0}</Text>
                      <Text variant="bodySm" tone="subdued">Completed</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingLg" color="critical">{queueStats.failed || 0}</Text>
                      <Text variant="bodySm" tone="subdued">Failed</Text>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <Text variant="headingLg" color="base">{queueStats.total || 0}</Text>
                      <Text variant="bodySm" tone="subdued">Total</Text>
                    </div>
                  </InlineStack>
                </div>
              )}
          
          {/* Filter/Status Tabs and Search Bar Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px' }}>
            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <ButtonGroup>
                <Button
                  onClick={() => { 
                    setQueueStatusTab('all'); 
                    setPage(1);
                  }}
                  variant="secondary"
                  tone={queueStatusTab === 'all' ? 'success' : undefined}
                >
                  All
                </Button>
                <Button
                  onClick={() => { setQueueStatusTab('queued'); setPage(1); }}
                  variant="secondary"
                  tone={queueStatusTab === 'queued' ? 'success' : undefined}
                >
                  Queued
                </Button>
                <Button
                  onClick={() => { setQueueStatusTab('running'); setPage(1); }}
                  variant="secondary"
                  tone={queueStatusTab === 'running' ? 'success' : undefined}
                >
                  Running
                </Button>
                <Button
                  onClick={() => { setQueueStatusTab('completed'); setPage(1); }}
                  variant="secondary"
                  tone={queueStatusTab === 'completed' ? 'success' : undefined}
                >
                  Completed
                </Button>
                <Button
                  onClick={() => { setQueueStatusTab('failed'); setPage(1); }}
                  variant="secondary"
                  tone={queueStatusTab === 'failed' ? 'success' : undefined}
                >
                  Failed
                </Button>
              </ButtonGroup>
              {/* Refresh Button */}
              <Button
                icon={RefreshMajor}
                onClick={fetchQueueData}
                variant="tertiary"
                tone="base"
                accessibilityLabel="Refresh queue"
                style={{ marginLeft: 8 }}
              />
            </div>
            {/* Search Bar */}
            {queueItems.length > 0 && (
              <div style={{ minWidth: 260 }}>
                <TextField
                    label="Search by job ID, generation ID, or name..."
                    value={queueSearchQuery}
                    onChange={(val) => {
                      setPage(1);
                      setQueueSearchQuery(val);
                    }}
                    placeholder="Search by job ID, generation ID, or name..."
                    autoComplete="off"
                    prefix={
                      <span className="Polaris-Icon">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    }
                    labelHidden
                  />
              </div>
            )}
          </div>
          
          {/* Queue Table */}
          <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              {filteredQueueItems.length > 0 && (
                <thead>
                  <tr>
                    {headings.map((heading, idx) => (
                      <th key={idx} style={{
                        textAlign: 'center',
                        padding: '12px 8px',
                        fontWeight: 600,
                        color: '#6d7175',
                        fontSize: 13,
                        background: '#fafbfc',
                        borderBottom: '1px solid #e3e3e3',
                        minWidth: 120,
                      }}>{heading}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {isLoadingQueue ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Spinner size="large" />
                        <Text variant="bodyMd" tone="subdued">Loading queue...</Text>
                      </BlockStack>
                    </td>
                  </tr>
                ) : filteredQueueItems.length > 0 ? (
                  paginatedItems.map((item, index) => (
                    <tr key={item.jobId || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6d7175', fontSize: 13, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span title={item.generationId}>{item.generationId || 'N/A'}</span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6d7175', fontSize: 13, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span title={item.jobId}>{item.jobId || 'N/A'}</span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6d7175', fontSize: 13 }}>
                        {item.name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {getStatusBadge(item.status)}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center', color: '#6d7175', fontSize: 13 }}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {item.status === 'failed' && (
                          <Button
                            size="micro"
                            icon={ReplayMinor}
                            onClick={() => handleRetryJob(item.jobId)}
                            loading={retryingJobs.has(item.jobId)}
                            disabled={retryingJobs.has(item.jobId)}
                            accessibilityLabel={`Retry job ${item.jobId}`}
                            tone="success"
                          >
                            Retry
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ padding: '60px 16px', textAlign: 'center' }}>
                      <BlockStack gap="400" align="center">
                        <Icon 
                          source={ContentMinor} 
                          color="subdued" 
                          className="empty-state-big-icon"
                        />
                        <Text variant="bodyMd" tone="subdued">No queue items found</Text>
                      </BlockStack>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '24px 0' }}>
              <ButtonGroup variant="segmented">
                <Button
                  icon={ChevronLeftMinor}
                  disabled={!hasPrevPage || page === 1}
                  onClick={() => setPage(page - 1)}
                />
                {getVisiblePages().map(pageNum => (
                  <Button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    variant={page === pageNum ? 'primary' : 'secondary'}
                  >
                    {pageNum}
                  </Button>
                ))}
                <Button
                  icon={ChevronRightMinor}
                  disabled={!hasNextPage || page === totalPages}
                  onClick={() => setPage(page + 1)}
                />
              </ButtonGroup>
              <div style={{ marginTop: 8 }}>
                <Text variant="bodySm" tone="subdued">
                  Page {currentPage} of {totalPages} ({totalItems} total items)
                </Text>
              </div>
            </div>
          )}
            </BlockStack>
          </div>
        </Card>
      </Layout.Section>
    </Layout>
  );
};

export default GenerationQueuePage;